import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useId } from 'react';
import { Typography } from 'antd';
import { reportBlockedPress, useBlockedPress } from '../antd/blocked-reason.js';
import { Tooltip } from '../antd/overlays.js';
import { Icon } from '../icons/icon.js';
export function HintText({ hint, children, type, size = 'inherit', placement, className, style, }) {
    return (_jsx(Tooltip, { title: hint, kind: "help", tapToPin: true, clickToPin: true, placement: placement, children: _jsx(Typography.Text, { type: type, tabIndex: 0, "aria-description": typeof hint === 'string' ? hint : undefined, className: ['bk-hint-text', size === 'caption' ? 'bk-text-caption' : null, className]
                .filter(Boolean)
                .join(' '), style: style, children: children }) }));
}
/**
 * The primary cell text of a row that opens a detail view. It stops the click
 * from bubbling, so a table that ALSO opens the drawer from `onRow` for the
 * pointer does not open it twice.
 */
export function RowTrigger({ onOpen, children, className, ...rest }) {
    return (_jsx("button", { ...rest, type: "button", className: ['bk-row-trigger', className].filter(Boolean).join(' '), onClick: (event) => {
            event.stopPropagation();
            onOpen();
        }, children: children }));
}
const stopPropagation = (event) => {
    event.stopPropagation();
};
/** Keys that would otherwise activate an ancestor (a sorter, a Collapse header, a tab). */
const stopActivationKeys = (event) => {
    if (event.key === 'Enter' || event.key === ' ' || event.key === 'Spacebar') {
        event.stopPropagation();
    }
};
/**
 * An explicit «what is this?» ⓘ. A real, focusable button whose explanation is
 * its `aria-description`, so screen readers read it without opening anything.
 * Hover opens it after 300 ms, keyboard focus at once, and a tap or click pins
 * it for reading until an outside press, Esc or blur. Clicks and activation
 * keys never reach an ancestor, so a misplaced ⓘ cannot toggle a sorter, a
 * Collapse or a label's control.
 *
 * Never put one inside a tab, a menu item, a button, a Collapse header, a
 * sortable header or a `<label>` — use the panel body, or `useLabelHint`.
 */
export function InfoHint({ topic, children, placement, size = 16 }) {
    return (_jsx(Tooltip, { title: children, kind: "help", tapToPin: true, clickToPin: true, placement: placement, children: _jsx("button", { type: "button", className: "bk-info-hint", "aria-label": `راهنما: ${topic}`, "aria-description": children, onClick: stopPropagation, onPointerDown: stopPropagation, onKeyDown: stopActivationKeys, children: _jsx(Icon, { name: "info", size: size }) }) }));
}
/**
 * The reason a non-kit control is disabled. The wrapper, not the control,
 * takes focus while blocked (a disabled control cannot), carries the reason as
 * its `aria-description`, shows it on hover, keyboard focus and tap, and
 * swallows pointer events for the child. One tree whether or not a reason is
 * present, so the control is never remounted.
 */
export function DisabledReason({ reason, children, block }) {
    const blocked = reason ? reason : null;
    const onBlockedPress = useBlockedPress();
    return (_jsx(Tooltip, { title: blocked ?? '', kind: "help", tapToPin: true, children: _jsx("span", { className: block ? 'bk-disabled-reason bk-disabled-reason--block' : 'bk-disabled-reason', "data-blocked": blocked ? '' : undefined, tabIndex: blocked ? 0 : undefined, "aria-description": blocked ?? undefined, onClick: blocked ? (event) => reportBlockedPress(onBlockedPress, event, blocked) : undefined, 
            // The wrapper is a span: Enter/Space on it synthesise no click, so the
            // keyboard press is reported from the key itself (never prevented).
            onKeyDown: blocked
                ? (event) => {
                    const activates = event.key === 'Enter' || event.key === ' ';
                    if (activates && !event.repeat && !event.nativeEvent.isComposing) {
                        reportBlockedPress(onBlockedPress, event, blocked);
                    }
                }
                : undefined, children: children }) }));
}
/** The element is actually cut: wider (one line) or taller (a clamp) than its box. */
function isTruncated(el, lines) {
    return lines > 1 ? el.scrollHeight > el.clientHeight + 1 : el.scrollWidth > el.clientWidth + 1;
}
/**
 * Text clamped to `lines`, with the full value in a label tooltip only when it
 * is actually cut. A mouse rest or a touch hold opens it; the full text is in
 * the DOM for screen readers, and keyboard users get it where the row opens. An
 * identity column (a patient, a procedure) uses `lines={2}` so realistic names
 * are fully visible and only pathological lengths clamp.
 */
export function TruncatedText({ children, lines = 1, as = 'span', className, placement, }) {
    const Tag = as;
    const classes = [
        'bk-truncated-text',
        lines > 1 ? 'bk-truncated-text--lines' : null,
        className ?? null,
    ]
        .filter(Boolean)
        .join(' ');
    return (_jsx(Tooltip, { title: children, kind: "label", placement: placement, shouldOpen: (el) => isTruncated(el, lines), children: _jsx(Tag, { className: classes, style: lines > 1 ? { WebkitLineClamp: lines } : undefined, children: children }) }));
}
/**
 * A form-field label with an ⓘ that does NOT become part of the control's
 * accessible name. antd renders `Form.Item label` inside `<label for>`, and a
 * button inside a label joins the control's name; `aria-labelledby` pointing
 * at the text-only span wins over `<label for>`, so the name is exactly the
 * label text while a click on the text still focuses the control. The ⓘ
 * stops propagation, so clicking it never toggles a Switch or a Checkbox.
 *
 *     const { label, controlProps } = useLabelHint({ label: 'سنجاق', hint: '…' });
 *     <Form.Item label={label}><Switch {...controlProps} /></Form.Item>
 */
export function useLabelHint({ label, hint, topic }) {
    const textId = useId();
    return {
        label: (_jsxs("span", { className: "bk-label-hint", children: [_jsx("span", { id: textId, children: label }), _jsx(InfoHint, { topic: topic ?? label, children: hint })] })),
        controlProps: { 'aria-labelledby': textId },
    };
}
//# sourceMappingURL=affordances.js.map